/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { BenefitTypeOptions, NetworkStatuses, ReferentPriceTypeOptions } from '@strata/constants';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { InlineMessage } from '@strata/react-inline-message';
import { BidInput } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { FieldArray, Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { ListPageHeader } from 'components/ListPageHeader';
import { CREATE_ROUTE, GLOBAL_SEGMENT_ID } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { mapSuppliersToOptions } from 'services/NPISupport';

const mapProductsToBrandAndGenericProducts = (configuredProductsList) => {
    const products = [];

    configuredProductsList?.forEach((product) => {
        // The segment endpoint keys products by product_service_oid, the universal one by oid
        const productServiceOid = product.product_service_oid ?? product.oid;
        products.push({
            productServiceOid,
            name: product.brand_product_description,
            secondaryData: 'Brand',
            isBrand: true,
        });

        if (
            product.generic_product_description &&
            product.brand_product_description.toLowerCase() !==
                product.generic_product_description?.toLowerCase()
        ) {
            products.push({
                productServiceOid,
                name: product.generic_product_description,
                secondaryData: 'Generic',
                isBrand: false,
            });
        }
    });
    // Filter out duplicate names when multiple policies are configured
    return [...new Map(products.map((product) => [product.name, product])).values()].sort((a, b) =>
        sortAsc(a.name, b.name),
    );
};

const MAX_UNIVERSAL_SUPPLIERS = 3;

const BackstopRateInput = ({ isCreate, isUsed }) => {
    const formik = useFormikContext();

    const queryParams = useMemo(() => {
        const params = {
            payer_id: formik.values.payerId,
            product_service_oid: formik.values.productServiceOid,
            is_brand: formik.values.isBrand,
        };
        // Don't add null placeOfService to query string
        if (formik.values.placeOfService) {
            params.place_of_service = formik.values.placeOfService;
        }
        return params;
    }, [
        formik.values.payerId,
        formik.values.productServiceOid,
        formik.values.isBrand,
        formik.values.placeOfService,
    ]);
    const rateRules = useGet(
        !isUsed &&
            formik.values.payerId &&
            formik.values.productServiceOid &&
            formik.values.isBrand != null
            ? '/ratesetter/rate_rules'
            : null,
        queryParams,
    );
    const rateRule = rateRules?.filter(
        (rule) =>
            (rule.benefit === formik.values.benefit || rule.benefit == null) &&
            (rule.segment_id === formik.values.segmentId || rule.segment_id == null),
    )[0];

    useEffect(() => {
        if (
            (isCreate || rateRule?.referent_price_type) &&
            formik.values.referentPriceType !== rateRule?.referent_price_type
        ) {
            formik.setFieldValue('referentPriceType', rateRule?.referent_price_type);
        }
    }, [isCreate, rateRule?.referent_price_type, formik]);

    return (
        <BidInput
            id="suppliers.0.backstopRate"
            label="Contract Rate"
            disabled={isUsed}
            referentPriceType={formik.values.referentPriceType}
            minAllowedBid={rateRule?.default_rate ? Number(rateRule.default_rate) : undefined}
        />
    );
};
BackstopRateInput.propTypes = {
    isCreate: PropTypes.bool.isRequired,
    isUsed: PropTypes.bool.isRequired,
};

// There are no universal rate rules, so a universal config picks its own referent and has no minimum rate
const AssignedPharmacies = ({ supplierOptions = undefined, isUsed }) => {
    const formik = useFormikContext();

    return (
        <>
            <ListPageHeader pageTitle="Assigned Pharmacy" />
            <div className="margin__bottom--medium">
                Assignments will be made according to the priority order set here.
            </div>
            <RadioButtonGroupFormik
                name="referentPriceType"
                label="Referent"
                required
                inlineItems
                disabled={isUsed}
                options={ReferentPriceTypeOptions}
            />
            <FieldArray name="suppliers">
                {(arrayHelpers) => (
                    <>
                        {formik.values.suppliers.map((supplier, index, suppliers) => (
                            <div
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                className="flex__gap--small"
                            >
                                <span className="sds_backstop-pharmacy__priority">
                                    {index + 1}.
                                </span>
                                <div style={{ flexGrow: 1 }}>
                                    <SuggestSingleSelectFormik
                                        id={`suppliers.${index}.supplierNpi`}
                                        label="Pharmacy"
                                        required
                                        disabled={isUsed}
                                        isLoading={!supplierOptions}
                                        // A pharmacy can only hold one priority
                                        values={supplierOptions?.filter(
                                            (option) =>
                                                option.value === supplier.supplierNpi ||
                                                !suppliers.some(
                                                    ({ supplierNpi }) =>
                                                        supplierNpi === option.value,
                                                ),
                                        )}
                                        selectedItem={supplierOptions?.find(
                                            (option) => option.value === supplier.supplierNpi,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                `suppliers.${index}.supplierNpi`,
                                                newValue?.value,
                                            )
                                        }
                                    />
                                </div>
                                <div style={{ flexGrow: 1 }}>
                                    <BidInput
                                        id={`suppliers.${index}.backstopRate`}
                                        label="Contract Rate"
                                        disabled={isUsed}
                                        referentPriceType={formik.values.referentPriceType}
                                    />
                                </div>
                                {!isUsed && suppliers.length > 1 && (
                                    <Icon
                                        className="margin__top--large"
                                        title="Remove pharmacy"
                                        icon={IconNames.CROSS}
                                        intent={Icon.Intent.SECONDARY}
                                        onClick={() => arrayHelpers.remove(index)}
                                    />
                                )}
                            </div>
                        ))}
                        {!isUsed && formik.values.suppliers.length < MAX_UNIVERSAL_SUPPLIERS && (
                            <div>
                                <ActionButtonText
                                    label="Add Another Pharmacy"
                                    leftIcon={IconNames.ADD}
                                    onClick={() => arrayHelpers.push({})}
                                />
                            </div>
                        )}
                    </>
                )}
            </FieldArray>
        </>
    );
};
AssignedPharmacies.propTypes = {
    supplierOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
    isUsed: PropTypes.bool.isRequired,
};

// eslint-disable-next-line complexity
export const BackstopPharmacyDetails = () => {
    const { segmentId, backstopPharmacyId } = useParams();

    const navigate = useNavigate();

    const goHome = () => navigate(`/route-management/post-live-referral-assignment/${segmentId}`);

    const isCreate = backstopPharmacyId === CREATE_ROUTE;
    const isUniversal = segmentId === GLOBAL_SEGMENT_ID;

    const backstopPharmacyConfig = useGet(
        !isCreate ? `/assignment/backstop_pharmacies/${backstopPharmacyId}` : null,
    );

    const segments = useGet('/reference/payer_segments');
    const segmentOptions = segments && [
        {
            name: 'All (universal)',
            value: GLOBAL_SEGMENT_ID,
            payerId: GLOBAL_SEGMENT_ID,
        },
        ...segments
            .map((segment) => ({
                name: segment.segment_name,
                secondaryData: segment.segment_id,
                value: segment.segment_id,
                payerId: segment.payer_id,
            }))
            .sort((a, b) => sortAsc(a.name, b.name)),
    ];
    const selectedSegment = segmentOptions?.find((segment) => segment.value === segmentId);

    const suppliers = useGet('/reference/suppliers/parents');
    const supplierOptions = mapSuppliersToOptions(suppliers)?.filter(
        (supplier) => supplier.network_status?.trim() !== NetworkStatuses.INACTIVE,
    );

    const placesOfService = useGet('/reference/product_benefit_configs/places');
    const placeOfServiceOptions = placesOfService
        ? [
              { name: 'All Places of Service', value: null },
              ...placesOfService.map((place) => ({
                  name: place.description,
                  value: place.code,
              })),
          ]
        : undefined;

    const productServices = useGet(
        isUniversal
            ? '/reference/product_benefit_configs/'
            : `/reference/product_benefit_configs/segments/${segmentId}`,
    );
    const productOptions = mapProductsToBrandAndGenericProducts(productServices);
    const backstopReasons = useGet('/assignment/backstop_pharmacies/backstop_reason/values');

    const isLoading = !(isCreate || backstopPharmacyConfig);
    const isUsed = !!backstopPharmacyConfig?.is_used;

    return (
        <Formik
            initialValues={{
                segmentId,
                payerId: selectedSegment?.payerId,
                benefit: backstopPharmacyConfig?.benefit,
                productServiceOid: backstopPharmacyConfig?.product_service_oid,
                isBrand: backstopPharmacyConfig?.is_brand,
                placeOfService: backstopPharmacyConfig?.place_of_service ?? null,
                // A segment config has a single pharmacy; a universal config ranks one or more
                suppliers: backstopPharmacyConfig?.pharmacies
                    ? [...backstopPharmacyConfig.pharmacies]
                          .sort((a, b) => a.rank - b.rank)
                          .map((pharmacy) => ({
                              supplierNpi: pharmacy.supplier_npi,
                              backstopRate: pharmacy.backstop_rate
                                  ? Decimal.mul(pharmacy.backstop_rate, 100).toNumber()
                                  : undefined,
                          }))
                    : [{}],
                referentPriceType: backstopPharmacyConfig?.referent_price_type,
                backstopReason: backstopPharmacyConfig?.backstop_reason,
                backstopStartDate: backstopPharmacyConfig?.backstop_start_date,
                backstopEndDate: backstopPharmacyConfig?.backstop_end_date,
            }}
            enableReinitialize
            validationSchema={Yup.object({
                segmentId: Yup.string().required(validationStrings.requiredField.errorMessage),
                payerId: Yup.string().required(validationStrings.requiredField.errorMessage),
                benefit: Yup.string().required(validationStrings.requiredField.errorMessage),
                placeOfService: Yup.string()
                    .required(validationStrings.requiredField.errorMessage)
                    .nullable(),
                productServiceOid: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                isBrand: Yup.boolean().required(validationStrings.requiredField.errorMessage),
                suppliers: Yup.array()
                    .of(
                        Yup.object({
                            supplierNpi: Yup.string().required(
                                validationStrings.requiredField.errorMessage,
                            ),
                            backstopRate: BidInput.validator().required(
                                validationStrings.requiredField.errorMessage,
                            ),
                        }),
                    )
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
                referentPriceType: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                backstopReason: Yup.string().required(validationStrings.requiredField.errorMessage),
                backstopStartDate: DateInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                backstopEndDate: DateInputFormik.validator(),
            })}
            onSubmit={async (values) => {
                try {
                    const body = {
                        segment_id: isUniversal ? null : values.segmentId,
                        benefit: values.benefit,
                        place_of_service: values.placeOfService,
                        product_service_oid: values.productServiceOid,
                        is_brand: values.isBrand,
                        referent_price_type: values.referentPriceType,
                        pharmacies: values.suppliers.map((supplier, index) => ({
                            supplier_npi: supplier.supplierNpi,
                            // Rank is the row's position on screen, starting at 1
                            rank: index + 1,
                            backstop_rate: Decimal.div(supplier.backstopRate, 100).toNumber(),
                        })),
                        backstop_reason: values.backstopReason,
                        backstop_start_date: formatDate(
                            values.backstopStartDate,
                            DateFormats.HYPHEN_DATE,
                        ),
                        backstop_end_date: formatDate(
                            values.backstopEndDate,
                            DateFormats.HYPHEN_DATE,
                        ),
                    };

                    if (isCreate) {
                        await post('/assignment/backstop_pharmacies', body);
                    } else {
                        // note: the backstop_pharmacy_id *isn't* actually a pharmacy ID, this is
                        //  an ID for a row in the config table
                        await put('/assignment/backstop_pharmacies', {
                            ...body,
                            backstop_pharmacy_id: backstopPharmacyId,
                        });
                    }

                    SuccessToast({
                        message: `Post Live Referral Assignment ${
                            isCreate ? 'created' : 'updated'
                        } successfully.`,
                    });

                    goHome();
                } catch (err) {
                    handleError('Error saving Post Live Referral Assignment.', err);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    isLoading={isLoading}
                    formTitle={`${isCreate ? 'Create' : 'Edit'} ${isUniversal ? 'Universal' : 'Post Live Referral'} Assignment`}
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onSecondaryButtonClick={goHome}
                    formLeftContent={
                        <>
                            {isUniversal && (
                                <div className="margin__bottom--small">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.INFO}
                                        title="Universal Assignment"
                                        label="Changes made here will affect assignment for all payer segments."
                                    />
                                </div>
                            )}
                            <SuggestSingleSelectFormik
                                id="segmentId"
                                label="Payer Segment"
                                required
                                disabled
                                isLoading={!segmentOptions}
                                values={segmentOptions}
                                selectedItem={segmentOptions?.find(
                                    (segment) => segment.value === formik.values.segmentId,
                                )}
                                onChange={(newValue) => {
                                    void Promise.all([
                                        formik.setFieldValue('segmentId', newValue?.value),
                                        formik.setFieldValue('payerId', newValue?.payerId),
                                    ]);
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="productServiceOid"
                                label="Product"
                                required
                                disabled={!isCreate}
                                isLoading={!productServices}
                                values={productOptions}
                                selectedItem={productOptions.find(
                                    (product) =>
                                        product.productServiceOid ===
                                            formik.values.productServiceOid &&
                                        product.isBrand === formik.values.isBrand,
                                )}
                                onChange={(newValue) => {
                                    void Promise.all([
                                        formik.setFieldValue(
                                            'productServiceOid',
                                            newValue?.productServiceOid,
                                        ),
                                        formik.setFieldValue('isBrand', newValue?.isBrand),
                                    ]);
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="benefit"
                                label="Benefit"
                                required
                                disabled={!isCreate}
                                values={BenefitTypeOptions}
                                selectedItem={BenefitTypeOptions.find(
                                    ({ value }) => value === formik.values.benefit,
                                )}
                                onChange={(newValue) =>
                                    formik.setFieldValue('benefit', newValue?.value)
                                }
                            />
                            <SuggestSingleSelectFormik
                                id="placeOfService"
                                label="Place Of Service"
                                required
                                disabled={!isCreate}
                                isLoading={!placeOfServiceOptions}
                                values={placeOfServiceOptions}
                                selectedItem={placeOfServiceOptions?.find(
                                    ({ value }) => value === formik.values.placeOfService,
                                )}
                                onChange={(newValue) =>
                                    formik.setFieldValue('placeOfService', newValue?.value)
                                }
                            />
                            {!isUniversal && (
                                <>
                                    <SuggestSingleSelectFormik
                                        id="suppliers.0.supplierNpi"
                                        label="Pharmacy"
                                        required
                                        disabled={isUsed}
                                        isLoading={!supplierOptions}
                                        values={supplierOptions}
                                        selectedItem={supplierOptions?.find(
                                            (supplier) =>
                                                supplier.value ===
                                                formik.values.suppliers[0]?.supplierNpi,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'suppliers.0.supplierNpi',
                                                newValue?.value,
                                            )
                                        }
                                    />
                                    <BackstopRateInput isCreate={isCreate} isUsed={isUsed} />
                                </>
                            )}
                            <SuggestSingleSelectFormik
                                id="backstopReason"
                                label="Reason"
                                required
                                disabled={isUsed}
                                values={backstopReasons}
                            />
                            <DateInputFormik
                                id="backstopStartDate"
                                label="Start Date"
                                required
                                minDate={
                                    !dayjs(formik.initialValues.backstopStartDate).isBefore(
                                        dayjs(),
                                        'day',
                                    )
                                        ? dayjs().startOf('day').toDate()
                                        : undefined
                                }
                                disabled={
                                    isUsed ||
                                    dayjs(formik.initialValues.backstopStartDate).isBefore(
                                        dayjs(),
                                        'day',
                                    )
                                }
                            />
                            <DateInputFormik
                                id="backstopEndDate"
                                label="End Date"
                                helperText="Assignment will remain active until 11:59 PM on the selected end date."
                                minDate={dayjs(formik.values.backstopStartDate)
                                    .startOf('day')
                                    .add(1, 'day')
                                    .toDate()}
                            />
                            {isUniversal && (
                                <AssignedPharmacies
                                    supplierOptions={supplierOptions}
                                    isUsed={isUsed}
                                />
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
